<template>
  <div class="tender-supply-rules">
    <!-- Header -->
    <div class="rules-header q-mb-md">
      <div class="row items-center">
        <q-icon name="rule" size="24px" color="indigo" class="q-mr-sm" />
        <div class="text-h6">Supply Scope Rules</div>
      </div>
      <q-btn
        color="indigo"
        icon="add"
        label="Add Tender Rule"
        size="sm"
        @click="showAddDialog = true"
      />
    </div>

    <q-linear-progress v-if="loading" indeterminate color="indigo" class="q-mb-md" />

    <!-- Global Rules Section (Read-Only) -->
    <q-expansion-item
      v-if="globalRules.length > 0"
      icon="public"
      label="Global Rules"
      :caption="`${globalRules.length} system-wide rules`"
      header-class="bg-blue-grey-1"
      default-opened
    >
      <q-list bordered separator>
        <q-item v-for="rule in globalRules" :key="`global-${rule.id}`">
          <q-item-section avatar>
            <q-icon
              :name="rule.is_excluded ? 'block' : 'check_circle'"
              :color="rule.is_excluded ? 'negative' : 'positive'"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label>
              {{ rule.target_value }}
              <q-badge
                :color="rule.target_type === 'device_part' ? 'primary' : 'secondary'"
                class="q-ml-sm"
              >
                {{ rule.target_type === 'device_part' ? 'Part' : 'BRICK' }}
              </q-badge>
            </q-item-label>
            <q-item-label caption>{{ rule.reason }}</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-badge color="blue-grey" outline label="Global" />
          </q-item-section>
        </q-item>
      </q-list>
    </q-expansion-item>

    <!-- Tender-Specific Rules Section (Editable) -->
    <q-expansion-item
      icon="gavel"
      label="Tender Rules"
      :caption="`${tenderRules.length} tender-specific rules`"
      header-class="bg-blue-1"
      class="q-mt-sm"
      default-opened
    >
      <template #header>
        <q-item-section avatar>
          <q-icon name="gavel" color="blue" />
        </q-item-section>
        <q-item-section>
          <q-item-label>Tender Rules</q-item-label>
          <q-item-label caption>{{ tenderRules.length }} tender-specific rules</q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-btn
            flat
            round
            dense
            icon="add"
            color="blue"
            size="sm"
            @click.stop="showAddDialog = true"
          >
            <q-tooltip>Add Tender Rule</q-tooltip>
          </q-btn>
        </q-item-section>
      </template>

      <div v-if="tenderRules.length === 0" class="empty-rules q-pa-md text-center text-grey-6">
        <q-icon name="rule_folder" size="48px" />
        <p>No tender-specific rules defined.</p>
        <q-btn
          outline
          color="indigo"
          icon="add"
          label="Add Rule"
          size="sm"
          @click="showAddDialog = true"
        />
      </div>

      <q-list v-else bordered separator>
        <q-item v-for="rule in tenderRules" :key="`tender-${rule.id}`">
          <q-item-section avatar>
            <q-icon
              :name="rule.is_excluded ? 'block' : 'check_circle'"
              :color="rule.is_excluded ? 'negative' : 'positive'"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label>
              {{ rule.target_value }}
              <q-badge
                :color="rule.target_type === 'device_part' ? 'primary' : 'secondary'"
                class="q-ml-sm"
              >
                {{ rule.target_type === 'device_part' ? 'Part' : 'BRICK' }}
              </q-badge>
              <q-badge
                v-if="!rule.is_excluded"
                color="positive"
                class="q-ml-sm"
              >
                Override
              </q-badge>
            </q-item-label>
            <q-item-label caption>{{ rule.reason }}</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn
              flat
              round
              dense
              icon="delete"
              color="negative"
              size="sm"
              @click="confirmDeleteRule(rule)"
            >
              <q-tooltip>Delete Rule</q-tooltip>
            </q-btn>
          </q-item-section>
        </q-item>
      </q-list>
    </q-expansion-item>

    <!-- Add Rule Dialog -->
    <q-dialog v-model="showAddDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="bg-indigo text-white">
          <div class="text-h6">
            <q-icon name="add_circle" class="q-mr-sm" />
            Add Tender Rule
          </div>
        </q-card-section>

        <q-card-section class="q-pt-lg">
          <!-- Target Type -->
          <div class="q-mb-md">
            <div class="text-subtitle2 text-grey-8 q-mb-sm">Target Type</div>
            <q-btn-toggle
              v-model="ruleForm.target_type"
              spread
              toggle-color="indigo"
              :options="[
                { label: 'Device Part', value: 'device_part' },
                { label: 'BRICK Class', value: 'brick_class' }
              ]"
            />
          </div>

          <!-- Device Part Selection -->
          <div v-if="ruleForm.target_type === 'device_part'" class="q-mb-md">
            <q-select
              v-model="ruleForm.target_value"
              :options="filteredDeviceParts"
              option-label="display_name"
              option-value="part_number"
              emit-value
              label="Select Device Part"
              outlined
              use-input
              input-debounce="300"
              clearable
              :loading="searchingParts"
              @filter="filterDeviceParts"
            >
              <template #no-option>
                <q-item>
                  <q-item-section class="text-grey">
                    Type to search device parts...
                  </q-item-section>
                </q-item>
              </template>
              <template #option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <q-item-label>{{ scope.opt.part_number }}</q-item-label>
                    <q-item-label caption>
                      {{ scope.opt.manufacturer }} {{ scope.opt.model }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>

          <!-- BRICK Class Selection -->
          <div v-else class="q-mb-md">
            <q-select
              v-model="ruleForm.target_value"
              :options="brickClassOptions"
              label="Select or Enter BRICK Class"
              outlined
              use-input
              new-value-mode="add-unique"
              clearable
            />
          </div>

          <!-- Reason -->
          <q-input
            v-model="ruleForm.reason"
            label="Reason"
            outlined
            placeholder="e.g., VFDs excluded per tender scope"
            class="q-mb-md"
          />

          <!-- Action Toggle -->
          <div class="q-mb-md">
            <div class="text-subtitle2 text-grey-8 q-mb-sm">Action</div>
            <q-btn-toggle
              v-model="ruleForm.is_excluded"
              spread
              toggle-color="indigo"
              :options="[
                { label: 'Exclude (By Others)', value: true, icon: 'block' },
                { label: 'Include (Override)', value: false, icon: 'check_circle' }
              ]"
            />
            <div class="text-caption text-grey-7 q-mt-xs">
              {{ ruleForm.is_excluded
                ? 'Mark matching items as "By Others" for all quotes in this tender'
                : 'Include matching items that would otherwise be excluded by global rules' }}
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Cancel" @click="closeDialog" />
          <q-btn
            color="indigo"
            label="Add Rule"
            :loading="saving"
            @click="saveRule"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Delete Confirmation -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="negative" text-color="white" />
          <span class="q-ml-sm">
            Delete this tender rule?<br>
            <strong>{{ deleteTarget?.target_value }}</strong>
          </span>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="showDeleteDialog = false" />
          <q-btn
            flat
            label="Delete"
            color="negative"
            :loading="deleting"
            @click="deleteRule"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { api } from '@/services/api';

  const props = defineProps({
    tenderId: {
      type: [Number, String],
      required: true,
    },
  });

  const emit = defineEmits(['rules-changed']);

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const saving = ref(false);
  const deleting = ref(false);
  const searchingParts = ref(false);
  const allRules = ref([]);
  const showAddDialog = ref(false);
  const showDeleteDialog = ref(false);
  const deleteTarget = ref(null);
  const filteredDeviceParts = ref([]);

  // Form - default is_excluded: true for tender rules (typically exclude items)
  const ruleForm = ref({
    target_type: 'brick_class',
    target_value: null,
    reason: '',
    is_excluded: true,
  });

  // Common BRICK classes
  const brickClassOptions = ref([
    'brick:Supply_Air_Fan',
    'brick:Return_Air_Fan',
    'brick:Variable_Frequency_Drive',
    'brick:Chiller',
    'brick:Boiler',
    'brick:Cooling_Tower',
    'brick:Pump',
    'brick:Heat_Exchanger',
    'brick:Damper',
    'brick:Valve',
  ]);

  // Computed - separate rules by source
  const globalRules = computed(() => {
    return allRules.value.filter((r) => r.source === 'global');
  });

  const tenderRules = computed(() => {
    return allRules.value.filter((r) => r.source === 'tender');
  });

  // Methods
  async function loadRules() {
    loading.value = true;
    try {
      const response = await api.get(`/tenders/${props.tenderId}/supply-rules`);
      allRules.value = Array.isArray(response) ? response : (response.data || []);
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load supply rules: ' + error.message,
      });
    } finally {
      loading.value = false;
    }
  }

  async function filterDeviceParts(val, update) {
    if (!val || val.length < 2) {
      update(() => {
        filteredDeviceParts.value = [];
      });
      return;
    }

    searchingParts.value = true;
    try {
      const response = await api.get('/device-parts/search', { q: val });
      const data = Array.isArray(response) ? response : (response.data || []);

      update(() => {
        filteredDeviceParts.value = data.map((part) => ({
          ...part,
          display_name: `${part.part_number} - ${part.manufacturer} ${part.model}`,
        }));
      });
    } catch (error) {
      console.error('Failed to search device parts:', error);
      update(() => {
        filteredDeviceParts.value = [];
      });
    } finally {
      searchingParts.value = false;
    }
  }

  function closeDialog() {
    showAddDialog.value = false;
    ruleForm.value = {
      target_type: 'brick_class',
      target_value: null,
      reason: '',
      is_excluded: true,
    };
  }

  async function saveRule() {
    if (!ruleForm.value.target_value) {
      $q.notify({
        type: 'warning',
        message: 'Please select or enter a target value',
      });
      return;
    }

    saving.value = true;
    try {
      await api.post(`/tenders/${props.tenderId}/supply-rules`, ruleForm.value);
      $q.notify({
        type: 'positive',
        message: 'Tender rule added successfully',
      });

      closeDialog();
      await loadRules();
      emit('rules-changed');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to add rule: ' + error.message,
      });
    } finally {
      saving.value = false;
    }
  }

  function confirmDeleteRule(rule) {
    deleteTarget.value = rule;
    showDeleteDialog.value = true;
  }

  async function deleteRule() {
    if (!deleteTarget.value) {return;}

    deleting.value = true;
    try {
      await api.delete(`/tenders/${props.tenderId}/supply-rules/${deleteTarget.value.id}`);
      $q.notify({
        type: 'positive',
        message: 'Tender rule deleted successfully',
      });

      showDeleteDialog.value = false;
      deleteTarget.value = null;
      await loadRules();
      emit('rules-changed');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to delete rule: ' + error.message,
      });
    } finally {
      deleting.value = false;
    }
  }

  // Watch for tenderId changes
  watch(() => props.tenderId, () => {
    if (props.tenderId) {
      loadRules();
    }
  });

  // Lifecycle
  onMounted(() => {
    if (props.tenderId) {
      loadRules();
    }
  });

  // Expose methods for parent
  defineExpose({
    loadRules,
  });
</script>

<style scoped>
.tender-supply-rules {
  padding: 8px;
}

.rules-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.empty-rules {
  background: #f5f7fa;
  border-radius: 8px;
}

.empty-rules p {
  margin: 8px 0;
}
</style>
